import React from 'react';
import classNames from 'classnames';
import ResolveImage from 'components/ResolveImage';
import { Listing } from 'models/Listing';
import { Link } from 'react-router-dom';
import utils, { checkIsTierPremium, isNewListing } from 'utils';
import StatusBadge from '../StatusBadge';
import ListingPrices from 'components/ListingPrices';
import { getImgUrl } from 'components/ResolveImage/utils';
import useLocationMapper from 'router/data/useLocationMapper';
import ListingActions from '../../../../components/ListingActions';

import './ListingCard.styles.scss';
import './ListingCard.styles.responsive.scss';

type ListingCardProps = {
  className?: string;
  listing: Listing;
  onWatchlisted: (isWatchlisted: boolean) => void;
};

const ListingCard: React.FC<ListingCardProps> = (props) => {
  const { className, listing, onWatchlisted } = props;

  const {
    listingTitle,
    location,
    dealLocation,
    revenue,
    industry,
    gallery,
    createdAt,
    slugName,
    businessDescription,
    EBITDA,
    averageStockValue,
    askingPriceSelect,
    stockAtValue,
    type,
    currency,
    tier,
    listingContact,
    amount,
    amountFrom,
    amountTo,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = listing;

  const classes = classNames('listing-card', className);
  const { locationLabelMapper } = useLocationMapper();

  const firmLogo = listingContact?.firmLogo?.url;

  const isListingPremium = checkIsTierPremium(tier);

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  return (
    <div className={classes}>
      <div className="listing-card__content">
        <div className="listing-card__content__top">
          <StatusBadge isNew={isNewListing(createdAt)} />
          <Link
            className="listing-card__content__top__title"
            to={redirectionListingLink}
          >
            {listingTitle}
          </Link>
          <Link
            to={redirectionListingLink}
            className="listing-card__content__top__listing-type"
          >
            <span>{locationLabelMapper[location?.state] || dealLocation}</span>
            <span>{industry}</span>
          </Link>
          <ListingPrices
            className={classNames({
              'premium-listing-prices': isListingPremium,
            })}
            askingPrice={askingPriceSelect}
            amount={amount}
            amountFrom={amountFrom}
            amountTo={amountTo}
            revenue={revenue}
            EBITDA={EBITDA}
            averageStockValue={averageStockValue}
            stockAtValue={stockAtValue}
            currency={currency}
            amountRequiredVisibleInfo={amountRequiredVisibleInfo}
            amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
            amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
            revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
            EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
            averageStockValueRequiredVisibleInfo={
              averageStockValueRequiredVisibleInfo
            }
          />
        </div>
        <Link
          className="listing-card__content__description"
          to={redirectionListingLink}
        >
          {businessDescription}
        </Link>

        <ListingActions listing={listing} onWatchlisted={onWatchlisted} />
      </div>

      <Link
        aria-label="Business details page"
        className="listing-card__gallery-img"
        to={redirectionListingLink}
      >
        <ResolveImage
          src={getImgUrl(gallery[0]?.url, '320')}
          alt="Listing gallery."
          shouldLazyLoad={false}
        />
        {isListingPremium && firmLogo && (
          <ResolveImage
            asCompanyLogo
            alt="Company logo."
            src={getImgUrl(firmLogo, '240')}
            shouldLazyLoad={false}
          />
        )}
      </Link>
    </div>
  );
};

export default ListingCard;
